import { ClickAwayListener } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import LanguageContext from "components/LanguageContext";
import MessageContext from "components/MessageContext";
import { listCityStyled, suggestionStyled } from "components/templatesComponents/StyledComponent";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import ComedecService from "services/ComedecService";
import { debounce, departementCodeRegex, postalCodeRegex } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const propsStyled = { top: { xs: 40, md: 48 }, left: 0, right: { xs: 40, md: 176 } };
const listboxId = "geoloc-search-results";

const SearchBarDsfr = (props) => {
  const { setPosition, setCityPosition, setDepartment, setGeoPoints, setBeforeSearch, setShowMoreCards } = props;

  const { language } = useContext(LanguageContext);
  const { displayError } = useContext(MessageContext);

  const [inputValue, setInputValue] = useState("");
  const [inputDepartment, setInputDepartment] = useState(null);
  const [newDepartment, setNewDepartment] = useState({});
  const [results, setResults] = useState({});
  const [cursor, setCursor] = useState(0);
  const [refList, setRefList] = useState({});
  const [load, setLoad] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);

  const { features = [] } = results || {};
  const { geometry = {}, properties = {} } = features[cursor] || {};
  const { coordinates = [] } = geometry || {};
  const { nom = "" } = properties || {};

  const newFeatures = useMemo(() => (inputDepartment ? features.slice(0, 1) : features), [inputDepartment, features]);
  const hasSuggestions = newFeatures.length > 0;
  const showSuggestions = hasSuggestions && searchExpanded;
  const activeOptionId = hasSuggestions ? `geoloc-search-option-${cursor}` : undefined;
  const suggestionsMessage = hasSuggestions
    ? `${newFeatures.length}${t[language].geolocalisation.suggestions_message[newFeatures.length > 1 ? 1 : 0]}`
    : "";
  const getOptionLabel = (item) => {
    const cityName = inputDepartment ? inputDepartment.nom : item.properties.nom;
    const departmentName = inputDepartment ? inputDepartment.code : item.properties.departement?.nom;

    return departmentName ? `${cityName}, ${departmentName}` : cityName;
  };

  const departmentsRef = useRef([]);

  const getInfo = (value, searchType) => {
    axios
      .get(`https://geo.api.gouv.fr/communes?${searchType}=${value}&fields=departement&boost=population&format=geojson`)
      .then(({ data }) => {
        setLoad(false);
        setResults(data);
        setShowMoreCards(false);
        setSearchExpanded(true);
      })
      .catch(() => {
        displayError(t[language].common.search.error_api_message);
        setResults([]);
      });
  };

  const handleClickPosition = (lat, long, city) => {
    setBeforeSearch(false);
    setPosition([lat, long]);
    setCityPosition(city);
    setGeoPoints([]);
    setResults([]);
    setInputDepartment(null);
    setInputValue(inputDepartment ? inputDepartment.nom : city);
    if (inputDepartment) {
      setDepartment(newDepartment);
    } else {
      setDepartment(null);
    }
  };

  const handleChange = async (e) => {
    const { value } = e.target;
    setInputValue(value);
    setCursor(0);
    if (value && value.length > 1) {
      setLoad(true);
      setResults([]);
      setSearchExpanded(true);
      if (value.match(departementCodeRegex)) {
        if (departmentsRef.current.length === 0) {
          departmentsRef.current = await ComedecService.getDepartments();
        }
        const { current: departments } = departmentsRef;
        const departmentByCode = departments.find((d) => d.code === value.toUpperCase());
        if (departmentByCode) {
          let newPostalCode;
          // départements avec arrondissements
          if (["13", "69", "75"].includes(value)) {
            newPostalCode = `${value}001`;
            // Polynésie Française
          } else if (value === "987") {
            newPostalCode = `${value}35`;
            // Corse
          } else if (value.match(/^2[aAbB]$/g)) {
            newPostalCode = 20000;
            // Métropole
          } else if (value.length === 2) {
            newPostalCode = `${value}000`;
            // DOM-TOM
          } else {
            newPostalCode = `${value}00`;
          }
          setInputDepartment(departmentByCode);
          setNewDepartment(departmentByCode);
          // recherche par département via le chef-lieu
          debounce(getInfo, 350)(newPostalCode, "codePostal");
        }
      } else if (value.match(postalCodeRegex)) {
        setInputDepartment(null);
        debounce(getInfo, 350)(value, "codePostal");
      } else if (value.match(/^[A-zÀ-ú\-\s]*$/)) {
        setInputDepartment(null);
        debounce(getInfo, 350)(value, "nom");
      } else {
        setLoad(false);
      }
    } else {
      setResults([]);
    }
  };

  useEffect(() => {
    const refs = {};
    if (features?.length > 0) {
      features.forEach((item, i) => {
        refs[i] = React.createRef();
      });
      setRefList(refs);
    }
  }, [features]);

  const handleKey = (e) => {
    const ref = refList[cursor] && refList[cursor].current;
    // setMouseOver(false);
    if (e.keyCode === 38 /* Arrow-up */ && cursor > 0) {
      setCursor((prevState) => prevState - 1);
      scrollIntoView(ref, {
        scrollMode: "if-needed",
        block: "end",
        inline: "nearest",
      });
    } else if (e.keyCode === 40 /* Arrow-down */ && cursor < newFeatures.length - 1) {
      setCursor((prevState) => prevState + 1);
      scrollIntoView(ref, {
        scrollMode: "if-needed",
        block: "start",
        inline: "nearest",
      });
    } else if (e.keyCode === 13 && coordinates.length >= 2) {
      /* Enter */ handleClickPosition(coordinates[1], coordinates[0], nom);
    }
  };

  const handleClickIcon = () => {
    if (coordinates.length >= 2) {
      handleClickPosition(coordinates[1], coordinates[0], nom);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setSearchExpanded(false)}>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <Box
          className="fr-search-bar fr-search-bar--lg"
          role="search"
          sx={
            load
              ? {
                  "&.fr-search-bar .fr-btn": {
                    justifyContent: "center",
                    width: { lg: 176 },
                    "&:before": { content: "none" },
                  },
                }
              : {}
          }
        >
          <label className="fr-label" htmlFor="geoloc-search-input" id="geoloc-search-input-label">
            {t[language].geolocalisation.input_search_label}
          </label>
          <input
            className="fr-input"
            aria-autocomplete="list"
            aria-controls={showSuggestions ? listboxId : undefined}
            aria-activedescendant={activeOptionId}
            placeholder={t[language].geolocalisation.search}
            id="geoloc-search-input"
            type="search"
            autoComplete="off"
            role="combobox"
            aria-expanded={showSuggestions}
            value={inputValue}
            onChange={handleChange}
            onFocus={() => setSearchExpanded(hasSuggestions)}
            onKeyDown={handleKey}
          />
          <p id="geoloc-search-input-messages" className="fr-sr-only" aria-live="polite">
            {suggestionsMessage}
          </p>
          <button title={t[language].geolocalisation.search} type="button" className="fr-btn" onClick={handleClickIcon}>
            {load ? <CircularProgress sx={{ color: "#fff" }} size={21} /> : t[language].geolocalisation.search}
          </button>
        </Box>
        {showSuggestions && (
          <Box
            id={listboxId}
            component="ul"
            role="listbox"
            aria-labelledby="geoloc-search-input-label"
            sx={listCityStyled(propsStyled)}
          >
            {newFeatures.map((item, i) => (
              <React.Fragment key={`${item.properties.nom}-${item.properties._score}`}>
                {item.geometry && (
                  <Box
                    id={`geoloc-search-option-${i}`}
                    component="li"
                    role="option"
                    aria-selected={cursor === i}
                    aria-label={getOptionLabel(item)}
                    ref={refList[i]}
                    onClick={() =>
                      handleClickPosition(
                        item.geometry.coordinates[1],
                        item.geometry.coordinates[0],
                        item.properties.nom,
                      )
                    }
                    sx={suggestionStyled(cursor === i)}
                  >
                    {inputDepartment ? inputDepartment.nom : item.properties.nom}
                    {item.properties.departement && (
                      <Box component="span" className="fr-text--sm fr-mb-0" sx={{ color: "var(--text-mention-grey)" }}>
                        {inputDepartment ? inputDepartment.code : item.properties.departement.nom}
                      </Box>
                    )}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

SearchBarDsfr.propTypes = {
  setPosition: PropTypes.func.isRequired,
  setBeforeSearch: PropTypes.func.isRequired,
  setCityPosition: PropTypes.func.isRequired,
  setDepartment: PropTypes.func.isRequired,
  setGeoPoints: PropTypes.func.isRequired,
  setShowMoreCards: PropTypes.func.isRequired,
};

export default SearchBarDsfr;
